import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState, type DragEvent } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { LimiteDeErro } from '../componentes/LimiteDeErro';
import { IndicadoresDoCalculo, TabelaDoCalculo } from '../componentes/ResultadoDoCalculo';
import {
  CampoDeMoeda,
  Aviso,
  Botao,
  Campo,
  CampoCnpj,
  CampoDeSelecao,
  CampoTelefone,
  Cartao,
  Selo,
  classesDeBotao,
} from '../componentes/ui';
import { useCalculoManual, useCalculoPorSped } from '../hooks/useCalculadora';
import {
  EXTENSOES_SPED_ACEITAS,
  MAXIMO_ARQUIVOS_SPED,
  VINCULOS_COM_A_EMPRESA,
  ehArquivoSpedAceito,
  linkDoSuporte,
  type Identificacao,
  type ItemCalculadoraManual,
  type ResultadoCalculadora,
  type SimulacaoRegistrada,
  type SituacaoDoCadastro,
  type VinculoComAEmpresa,
} from '../servicos/calculadora';
import { cnpjValido, telefoneValido } from '../servicos/documento';
import { formatarCnpj, formatarCompetencia, lerValorDigitado } from '../servicos/formato';
import { ErroDaApi } from './ErroDaApi';

/**
 * Calculadora pública de crédito de ICMS.
 *
 * ## RN-615 — nenhuma fórmula nesta tela
 *
 * Todos os valores exibidos (`totalSped`, `percentSped`, `valueSped`,
 * `percentBeneficio`, `valueBeneficio`, `total`) chegam prontos de
 * `POST /api/icms/public/calculadora-manual`, que só traduz o que o
 * `@deca/domain` calculou. Nem a tabela de percentuais por faixa nem o deságio
 * (D-002: fixo em 25%, configurável no servidor) existem aqui. O rodapé de
 * totais usa o objeto `totais` da própria resposta — o front não soma nem
 * arredonda nada que o usuário leia como valor fiscal.
 *
 * `lerValorDigitado` é a única conta desta tela, e não é fiscal: converte o que
 * a pessoa digitou (`1.234,56`) no número que vai no corpo da requisição.
 *
 * ## RN-616 — duas etapas
 *
 * `entrada` mostra o formulário; `resultado` mostra a tabela e permite calcular
 * de novo, como no fluxo `modoView` do legado.
 *
 * ## O que a tela guarda depois do cálculo
 *
 * A resposta inteira (`SimulacaoRegistrada`), não só o resultado. O `registroId`
 * é o pré-cadastro que a simulação criou — é para onde "Finalizar meu cadastro"
 * leva. A versão anterior o descartava, e o lead que queria seguir não tinha
 * botão nenhum: só o e-mail, se chegasse.
 */

type Etapa = 'identificacao' | 'entrada' | 'resultado';
type Modo = 'manual' | 'sped';

export function CalculadoraPublica(): JSX.Element {
  const [etapa, setEtapa] = useState<Etapa>('identificacao');
  // SPED é o caminho principal (era o único no Deca 1): o arquivo traz o
  // contribuinte e os valores apurados de verdade. Lançar valores é o atalho
  // para quem não tem o arquivo em mãos.
  const [modo, setModo] = useState<Modo>('sped');
  const [quem, setQuem] = useState<Identificacao | null>(null);
  const [simulacao, setSimulacao] = useState<SimulacaoRegistrada | null>(null);

  const aoCalcular = (registro: SimulacaoRegistrada): void => {
    setSimulacao(registro);
    setEtapa('resultado');
  };

  /**
   * Recomeçar apaga **também** a identificação.
   *
   * É o bug do Deca 1 que esta tela precisa não repetir: lá, quem preenchia os
   * dados uma vez e voltava seguia subindo SPED atrás de SPED sem se
   * identificar de novo — e as simulações seguintes ou ficavam penduradas no
   * primeiro contato ou não viravam lead nenhum. Cada simulação é um lead, e
   * um lead tem dono; zerar `quem` aqui é o que garante isso, e devolver o
   * fluxo para a etapa de identificação é o que torna a regra visível.
   */
  const recomecar = (): void => {
    setSimulacao(null);
    setQuem(null);
    setEtapa('identificacao');
  };

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <header className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-conteudo-primario sm:text-3xl">
          Calculadora de crédito de ICMS
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-conteudo-secundario">
          Simule quanto do ICMS apurado pode virar crédito negociável. O cálculo roda no servidor,
          com as mesmas regras usadas na operação.
        </p>
      </header>

      <LimiteDeErro area="a calculadora">
        {etapa === 'identificacao' || quem === null ? (
          <FormularioDeIdentificacao
            aoConfirmar={(dados) => {
              setQuem(dados);
              setEtapa('entrada');
            }}
          />
        ) : etapa === 'entrada' ? (
          <div className="flex flex-col gap-6">
            <QuemEsta quem={quem} aoTrocar={recomecar} />
            <SeletorDeModo modo={modo} aoTrocar={setModo} />
            {modo === 'manual' ? (
              <FormularioManual quem={quem} aoCalcular={aoCalcular} />
            ) : (
              <FormularioSped quem={quem} aoCalcular={aoCalcular} />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <Aviso tom="positivo" titulo="Simulação registrada">
              Enviamos o resultado para {quem.emailAddress}. Um consultor da MS3 Capital entra em
              contato para explicar como transformar esse crédito em caixa.
            </Aviso>
            <TabelaResultado resultado={simulacao?.resultado ?? null} aoRecomecar={recomecar} />
            {simulacao !== null && (
              <AcoesPosResultado
                registroId={simulacao.registroId}
                situacao={simulacao.situacaoDoCadastro}
                suporteWhatsapp={simulacao.suporteWhatsapp}
              />
            )}
          </div>
        )}
      </LimiteDeErro>
    </main>
  );
}

/**
 * Escolha da forma de entrada.
 *
 * Botões de alternância com `aria-pressed`, **não** um widget de abas. Abas
 * ARIA de verdade exigem `tabpanel` associado e navegação por seta com roving
 * tabindex; a versão anterior tinha `role="tab"` sem nenhum dos dois, o que
 * anuncia ao leitor de tela um comportamento que a tela não entrega. A
 * biblioteca de UI não tem abas de propósito (é o mesmo motivo de modal e menu
 * ficarem de fora), então aqui não se inventa uma.
 *
 * O destaque **não** acompanha a seleção: SPED é sempre o botão forte e
 * "Lançar valores" é sempre o sutil. Antes o botão ativo ganhava a cor
 * primária, e como a tela abria no manual, o caminho secundário parecia o
 * recomendado. O que diz qual está ativo é o `aria-pressed` e o card aberto
 * embaixo.
 */
function SeletorDeModo(props: {
  readonly modo: Modo;
  readonly aoTrocar: (modo: Modo) => void;
}): JSX.Element {
  const sped = props.modo === 'sped';

  return (
    <div
      role="group"
      aria-label="Forma de informar os dados"
      className="flex flex-wrap items-center gap-x-3 gap-y-2"
    >
      <Botao
        variante={sped ? 'primario' : 'secundario'}
        tamanho="pequeno"
        aria-pressed={sped}
        onClick={() => props.aoTrocar('sped')}
      >
        Enviar arquivos SPED
      </Botao>
      <span className="text-xs text-conteudo-sutil">
        Não tem os arquivos em mãos?{' '}
        <Botao
          variante="sutil"
          tamanho="pequeno"
          aria-pressed={!sped}
          onClick={() => props.aoTrocar('manual')}
          className="underline-offset-4 hover:underline"
        >
          Lançar valores manualmente
        </Botao>
      </span>
    </div>
  );
}

// ─── Lançamento manual ──────────────────────────────────────────────────────

/**
 * Uma linha só precisa de competência e valor — e os dois, quando a linha
 * existe. A versão anterior ignorava linha totalmente em branco e reclamava em
 * um aviso no topo; o problema aparecia longe do campo que o causou. Aqui cada
 * linha responde por si, com a mensagem embaixo do input errado.
 */
const esquemaManual = z.object({
  regime: z.enum(['', 'INDUSTRIAL', 'COMERCIAL']),
  /**
   * Empresa e CNPJ só existem aqui. No envio de SPED o contribuinte sai do
   * arquivo; no manual não há de onde tirar, e os dois seguem opcionais — quem
   * lança valores nem sempre sabe o CNPJ de cor, e o cadastro completa depois.
   */
  empresa: z.string().trim(),
  cnpj: z
    .string()
    .trim()
    .refine((c) => c === '' || cnpjValido(c), {
      message: 'CNPJ inválido — confira os dígitos.',
    }),
  itens: z
    .array(
      z.object({
        competencia: z
          .string()
          .min(1, 'Informe a competência.')
          // `type="month"` já produz `AAAA-MM`, que é o formato que o backend
          // valida. A checagem cobre o navegador que cai no input de texto.
          .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use o formato AAAA-MM.'),
        valor: z
          .string()
          .refine((texto) => lerValorDigitado(texto) !== null, 'Informe o ICMS apurado do mês.'),
      }),
    )
    .min(1, 'Informe ao menos um mês para calcular.'),
});

type CamposManual = z.infer<typeof esquemaManual>;

const LINHA_VAZIA = { competencia: '', valor: '' } as const;

function FormularioManual(props: {
  readonly quem: Identificacao;
  readonly aoCalcular: (registro: SimulacaoRegistrada) => void;
}): JSX.Element {
  const calculo = useCalculoManual();
  const [erroDoEnvio, setErroDoEnvio] = useState<unknown>(null);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CamposManual>({
    resolver: zodResolver(esquemaManual),
    defaultValues: { regime: '', empresa: '', cnpj: '', itens: [{ ...LINHA_VAZIA }] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'itens' });

  const enviar = handleSubmit(async (valores) => {
    setErroDoEnvio(null);

    // `flatMap` em vez de `?? 0`: o esquema já garante que todo valor converte,
    // e um zero fabricado aqui seria uma afirmação fiscal que ninguém digitou.
    const itens: ItemCalculadoraManual[] = valores.itens.flatMap((item) => {
      const totalSped = lerValorDigitado(item.valor);
      return totalSped === null ? [] : [{ competencia: item.competencia, totalSped }];
    });

    try {
      const registro = await calculo.mutateAsync({
        entrada: {
          itens,
          ...(valores.regime === '' ? {} : { regime: valores.regime }),
          ...(valores.empresa === '' ? {} : { empresa: valores.empresa }),
          ...(valores.cnpj === '' ? {} : { cnpj: valores.cnpj.replace(/\D/g, '') }),
        },
        quem: props.quem,
      });
      props.aoCalcular(registro);
    } catch (erro: unknown) {
      // Os erros por campo da API não casam com `itens.0.competencia`: o
      // backend fala em `itens[0].competencia`. O que ele mandar aparece na
      // lista do `ErroDaApi`, sem sumir e sem cair no campo errado.
      setErroDoEnvio(erro);
    }
  });

  return (
    <Cartao
      como="section"
      titulo="Lançar valores por mês"
      subtitulo="Informe o ICMS apurado de cada competência. É o mesmo dado que sai do SPED, no campo de total do período."
    >
      <form onSubmit={enviar} noValidate>
        {/* Sem arquivo, só o que a pessoa digita identifica a empresa. */}
        <div className="mb-5 grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Empresa"
            ajuda="Opcional. Sem o SPED, é assim que o consultor sabe de quem é o cálculo."
            autoComplete="organization"
            erro={errors.empresa?.message}
            {...register('empresa')}
          />
          <Controller
            control={control}
            name="cnpj"
            render={({ field }) => (
              <CampoCnpj
                rotulo="CNPJ"
                ajuda="Opcional."
                erro={errors.cnpj?.message}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>

        <fieldset className="min-w-0 border-0 p-0">
          <legend className="sr-only">Competências a calcular</legend>

          <div className="flex flex-col gap-3">
            {fields.map((campo, indice) => (
              <div key={campo.id} className="flex flex-wrap items-start gap-3">
                {/*
                  Competência viaja como par (ano, mês) em texto, nunca como
                  `Date`: é o que elimina a classe de bug do `converterData`
                  legado (D-001, RN-012).
                */}
                <Campo
                  rotulo={`Competência do mês ${indice + 1}`}
                  rotuloOculto={indice > 0}
                  type="month"
                  className="min-w-[10rem] flex-1"
                  erro={errors.itens?.[indice]?.competencia?.message}
                  {...register(`itens.${indice}.competencia` as const)}
                />

                <Controller
                  control={control}
                  name={`itens.${indice}.valor` as const}
                  render={({ field }) => (
                    <CampoDeMoeda
                      rotulo={`ICMS apurado no mês ${indice + 1}`}
                      rotuloOculto={indice > 0}
                      className="min-w-[11rem] flex-1"
                      erro={errors.itens?.[indice]?.valor?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />

                <Botao
                  variante="sutil"
                  onClick={() => remove(indice)}
                  disabled={fields.length === 1}
                  aria-label={`Remover o mês da linha ${indice + 1}`}
                  className={indice === 0 ? 'mt-6' : undefined}
                >
                  Remover
                </Botao>
              </div>
            ))}
          </div>
        </fieldset>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <Botao variante="secundario" onClick={() => append({ ...LINHA_VAZIA })}>
            Adicionar mês
          </Botao>

          {/* RN-013 — o regime escolhe a tabela de percentual, no domínio. */}
          <CampoDeSelecao
            rotulo="Regime"
            className="w-full sm:w-56"
            ajuda="Define a tabela de percentual aplicada."
            opcoes={[
              { valor: '', rotulo: 'Padrão do sistema' },
              { valor: 'COMERCIAL', rotulo: 'Comercial' },
              { valor: 'INDUSTRIAL', rotulo: 'Industrial' },
            ]}
            {...register('regime')}
          />
        </div>

        {erroDoEnvio !== null && (
          <div className="mt-4">
            <ErroDaApi titulo="O cálculo não foi feito" erro={erroDoEnvio} />
          </div>
        )}

        <div className="mt-6">
          <Botao type="submit" carregando={isSubmitting}>
            {isSubmitting ? 'Calculando…' : 'Calcular crédito'}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

// ─── Envio de SPED ──────────────────────────────────────────────────────────

/**
 * O envio de arquivo fica fora do react-hook-form de propósito: o estado é uma
 * lista de `File` acumulada por arrastar-e-soltar e por seleção, com dedupe e
 * lista de recusados — nada disso é campo de formulário, e embrulhar `FileList`
 * em RHF só acrescentaria uma camada entre o `input` e a chamada.
 */
function FormularioSped(props: {
  readonly quem: Identificacao;
  readonly aoCalcular: (registro: SimulacaoRegistrada) => void;
}): JSX.Element {
  const [arquivos, setArquivos] = useState<readonly File[]>([]);
  const [recusados, setRecusados] = useState<readonly string[]>([]);
  const [arrastando, setArrastando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const idEntrada = useId();
  const calculo = useCalculoPorSped();

  /** RN-613 — só `.txt`, arrastando ou selecionando. RN-640: sem ler o layout. */
  const receber = (lista: FileList | null): void => {
    if (lista === null) return;

    const novos: File[] = [];
    const fora: string[] = [];

    for (const arquivo of Array.from(lista)) {
      if (ehArquivoSpedAceito(arquivo)) novos.push(arquivo);
      else fora.push(arquivo.name);
    }

    setRecusados(fora);
    setAviso(null);
    setArquivos((atuais) => {
      // Reenviar o mesmo arquivo não deve duplicá-lo na lista.
      const existentes = new Set(atuais.map((a) => `${a.name}:${a.size}`));
      const ineditos = novos.filter((a) => !existentes.has(`${a.name}:${a.size}`));
      const juntos = [...atuais, ...ineditos];

      // O teto é do servidor (RN-102: 12 por simulação). Cortar aqui, avisando,
      // é melhor que deixar subir e voltar 400 depois do upload inteiro.
      if (juntos.length > MAXIMO_ARQUIVOS_SPED) {
        setAviso(
          `A simulação aceita ${MAXIMO_ARQUIVOS_SPED} arquivos (um ano fiscal). ` +
            `Os ${juntos.length - MAXIMO_ARQUIVOS_SPED} últimos foram deixados de fora.`,
        );
        return juntos.slice(0, MAXIMO_ARQUIVOS_SPED);
      }

      return juntos;
    });
  };

  const enviar = (): void => {
    // RN-614 — sem arquivo não há envio.
    if (arquivos.length === 0) {
      setAviso('Adicione ao menos um arquivo SPED (.txt) para calcular.');
      return;
    }
    // O servidor calcula, grava a simulação e guarda os `.txt`: o resultado que
    // volta já é o registrado, não uma prévia que alguém precisa confirmar.
    calculo.mutate({ arquivos, quem: props.quem }, { onSuccess: props.aoCalcular });
  };

  const aoSoltar = (evento: DragEvent<HTMLDivElement>): void => {
    evento.preventDefault();
    setArrastando(false);
    receber(evento.dataTransfer.files);
  };

  return (
    <Cartao
      como="section"
      titulo="Enviar arquivos SPED"
      subtitulo="Um arquivo por competência, no formato .txt exportado do SPED Fiscal."
    >
      {/* RN-619 — regra de negócio expressa como texto de ajuda. */}
      <p className="text-sm text-conteudo-secundario">
        Envie o SPED do <strong>ano anterior</strong> ao período que quer simular. Para estimar a
        economia do primeiro semestre de 2026, por exemplo, envie os arquivos de janeiro a junho de
        2025.
      </p>

      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={aoSoltar}
        className={`mt-4 rounded-painel border border-dashed px-6 py-10 text-center transition-colors ${
          arrastando ? 'border-acento-ciano bg-superficie-vidro' : 'border-borda'
        }`}
      >
        <p className="text-sm text-conteudo-secundario">
          Arraste os arquivos <span className="valor-fiscal text-xs">.txt</span> aqui
        </p>
        <p className="mt-1 text-xs text-conteudo-secundario">ou</p>

        {/*
          O input fica visível ao teclado (`sr-only` não remove do foco) e o
          label o aciona: dropzone só com mouse exclui quem navega por teclado.
        */}
        <input
          id={idEntrada}
          type="file"
          multiple
          accept={EXTENSOES_SPED_ACEITAS}
          onChange={(evento) => {
            receber(evento.target.files);
            // Permite reenviar o mesmo arquivo depois de removê-lo da lista.
            evento.target.value = '';
          }}
          className="sr-only"
        />
        <label
          htmlFor={idEntrada}
          className="mt-3 inline-flex h-11 cursor-pointer items-center rounded-card border border-borda bg-superficie-elevada px-4 text-sm font-medium text-conteudo-primario transition-colors hover:border-acento-ciano/50 hover:bg-superficie-vidro"
        >
          Selecionar arquivos
        </label>
      </div>

      {recusados.length > 0 && (
        <div className="mt-4">
          <Aviso tom="atencao" titulo="Arquivos ignorados">
            Só arquivos <span className="valor-fiscal text-xs">.txt</span> são aceitos:{' '}
            {recusados.join(', ')}
          </Aviso>
        </div>
      )}

      {arquivos.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2">
          {arquivos.map((arquivo) => (
            <li
              key={`${arquivo.name}:${arquivo.size}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-4 py-2"
            >
              <span className="text-sm text-conteudo-primario">{arquivo.name}</span>
              <span className="flex items-center gap-3">
                <Selo semPonto>{formatarTamanho(arquivo.size)}</Selo>
                <Botao
                  variante="sutil"
                  tamanho="pequeno"
                  aria-label={`Remover ${arquivo.name}`}
                  onClick={() =>
                    setArquivos((atuais) =>
                      atuais.filter((a) => !(a.name === arquivo.name && a.size === arquivo.size)),
                    )
                  }
                >
                  Remover
                </Botao>
              </span>
            </li>
          ))}
        </ul>
      )}

      {aviso !== null && (
        <div className="mt-4">
          <Aviso tom="atencao">{aviso}</Aviso>
        </div>
      )}

      {calculo.isError && (
        <div className="mt-4">
          <ErroDaApi titulo="O cálculo não foi feito" erro={calculo.error} />
        </div>
      )}

      <div className="mt-6">
        <Botao onClick={enviar} disabled={arquivos.length === 0} carregando={calculo.isPending}>
          {calculo.isPending ? 'Calculando…' : 'Calcular crédito'}
        </Botao>
      </div>
    </Cartao>
  );
}

function formatarTamanho(bytes: number): string {
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

// ─── Resultado ──────────────────────────────────────────────────────────────

// Indicadores e tabela vêm de `ResultadoDoCalculo`: a simulação aberta pelo
// comercial mostra o mesmo cálculo, com os mesmos rótulos.

function TabelaResultado(props: {
  readonly resultado: ResultadoCalculadora | null;
  readonly aoRecomecar: () => void;
}): JSX.Element {
  const resultado = props.resultado;

  if (resultado === null || resultado.linhas.length === 0) {
    return (
      <Cartao como="section" titulo="Nenhum mês elegível">
        <p className="text-sm text-conteudo-secundario">
          O cálculo rodou, mas nenhuma competência gerou crédito. Meses com apuração zerada ou
          negativa são descartados pela regra (RN-075).
        </p>
        <div className="mt-6">
          <Botao variante="secundario" onClick={props.aoRecomecar}>
            Calcular novamente
          </Botao>
        </div>
      </Cartao>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* `min-w-0`: sem isto o item de flex adota a largura do conteúdo e a
          tabela larga empurra a **página** para a rolagem horizontal, em vez de
          rolar dentro do próprio contêiner. */}
      <section aria-labelledby="titulo-resultado" className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2
              id="titulo-resultado"
              className="font-display text-lg font-semibold tracking-tight text-conteudo-primario"
            >
              Resultado da simulação
            </h2>
            <p className="mt-1 text-sm text-conteudo-secundario">
              Valores calculados no servidor. Esta tela apenas exibe.
            </p>
          </div>
          {/* Sutil: o próximo passo é finalizar o cadastro, não refazer a conta. */}
          <Botao variante="sutil" onClick={props.aoRecomecar}>
            Calcular novamente
          </Botao>
        </div>

        {resultado.contribuinte !== undefined && (
          <p className="text-sm text-conteudo-secundario">
            Empresa:{' '}
            <strong className="text-conteudo-primario">{resultado.contribuinte.razaoSocial}</strong>
            {resultado.contribuinte.cnpj !== '' && (
              <span className="valor-fiscal ml-2 text-xs">
                {formatarCnpj(resultado.contribuinte.cnpj)}
              </span>
            )}
          </p>
        )}

        <IndicadoresDoCalculo totais={resultado.totais} />
        <TabelaDoCalculo linhas={resultado.linhas} totais={resultado.totais} />
      </section>

      {resultado.ignorados.length > 0 && (
        <Cartao como="section" titulo="Meses não considerados">
          <ul className="flex flex-col gap-2 text-sm text-conteudo-secundario">
            {resultado.ignorados.map((ignorado) => (
              <li key={ignorado.competencia}>
                <span className="text-conteudo-primario">
                  {formatarCompetencia(ignorado.competencia)}
                </span>{' '}
                — {ignorado.motivo}
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {resultado.avisos.length > 0 && <AvisosDaLeitura avisos={resultado.avisos} />}
    </div>
  );
}

/* ─── Depois do resultado ─────────────────────────────────────────────────── */

/**
 * O que a pessoa faz com o número: a mesma barra do Deca 1, na mesma ordem.
 *
 * 1. "Finalizar meu cadastro" leva ao wizard do pré-cadastro que a simulação
 *    criou (`/cadastro/:registroId`) — é onde a empresa, o CPF e a senha entram,
 *    e onde quem marcou "gerencio uma carteira" vira cadastro na fila do admin
 *    (RN-322). O lead nunca vai para "Em aprovação" só por ter calculado.
 * 2. O texto, copiado do Deca 1: o e-mail com o resultado leva o mesmo link.
 * 3. O suporte pelo WhatsApp, com o número vindo do servidor (configuração,
 *    não código) e a mensagem pronta do Deca 1.
 *
 * ## Quem já tem cadastro
 *
 * O servidor reaproveita o pré-cadastro do e-mail mesmo quando ele já foi
 * analisado. Um cliente aprovado que volta a simular recebia "Finalizar meu
 * cadastro" e a promessa de um link para definir senha — e o wizard recusava
 * com 409. O Deca 1 tratava isso (`already_accounting` → "Acessar minha
 * Conta"); aqui a `situacaoDoCadastro` da resposta decide: só `RASCUNHO` tem
 * cadastro a finalizar, `APROVADO` vai para o login e `REJEITADO` fica só com
 * o suporte, que é o caminho que o próprio wizard indica nesse caso.
 */
function AcoesPosResultado(props: {
  readonly registroId: string;
  readonly situacao: SituacaoDoCadastro;
  readonly suporteWhatsapp: string;
}): JSX.Element {
  return (
    <Cartao como="section" titulo="Próximo passo">
      <div className="flex flex-col gap-5">
        {props.situacao === 'RASCUNHO' && (
          <>
            <div>
              <Link to={`/cadastro/${props.registroId}`} className={classesDeBotao('primario')}>
                Finalizar meu cadastro
              </Link>
            </div>

            <div className="flex flex-col gap-2 text-sm text-conteudo-secundario">
              <p>
                Você também recebeu um link para cadastrar suas informações e definir uma senha para
                concluir o seu cadastro.
              </p>
              <p>Por favor, siga as instruções no e-mail para finalizar o processo.</p>
            </div>
          </>
        )}

        {props.situacao === 'APROVADO' && (
          <>
            <div>
              <Link to="/entrar" className={classesDeBotao('primario')}>
                Acessar minha conta
              </Link>
            </div>

            <p className="text-sm text-conteudo-secundario">
              Este e-mail já tem cadastro aprovado. A simulação ficou registrada nele — entre na sua
              conta para acompanhar o aproveitamento.
            </p>
          </>
        )}

        {props.situacao === 'REJEITADO' && (
          <p className="text-sm text-conteudo-secundario">
            O cadastro deste e-mail foi recusado em uma análise anterior. Fale com o suporte para
            entender o motivo e como seguir.
          </p>
        )}

        <p className="text-sm text-conteudo-secundario">
          *** Se tiver alguma dúvida ou precisar de assistência, não hesite em nos contatar.
        </p>

        <div>
          <a
            href={linkDoSuporte(props.suporteWhatsapp)}
            target="_blank"
            rel="noreferrer"
            className={classesDeBotao('secundario')}
          >
            Contatar suporte
          </a>
        </div>
      </div>
    </Cartao>
  );
}

/* ─── Identificação ───────────────────────────────────────────────────────── */

/**
 * RN-101, RN-327 — Quem simula, pedido **antes** do cálculo.
 *
 * ## A troca de política
 *
 * A versão anterior mostrava o resultado primeiro e oferecia um formulário
 * opcional embaixo ("quer receber por e-mail?"), com o argumento de que exigir
 * identificação antes afasta o lead. O custo apareceu no uso: a maior parte das
 * simulações não deixava rastro, e Simulações de Crédito só via quem se dispôs
 * a preencher um formulário depois de já ter o número na tela.
 *
 * A calculadora é ferramenta de captação. Cada uso precisa produzir um lead com
 * contato utilizável, e por isso nome completo, e-mail e telefone passam a ser
 * requisito de entrada — validados aqui e de novo no servidor, que é quem manda.
 *
 * Empresa e CNPJ **não** ficam aqui: o contribuinte sai do SPED, que é
 * documento fiscal — o que a pessoa digitava aqui já era ignorado quando havia
 * arquivo. Os dois sobrevivem só no lançamento manual, onde não há de onde
 * tirar.
 *
 * O vínculo com a empresa (o `personOccupation` do Deca 1) é o que diz ao
 * consultor se ele vai ligar para o sócio ou para o contador; e o tick de
 * assessor é o que decide, lá no fim do cadastro, se ele passa pelo admin.
 */
function ehVinculo(valor: string): valor is VinculoComAEmpresa {
  return (VINCULOS_COM_A_EMPRESA as readonly string[]).includes(valor);
}

const esquemaIdentificacao = z.object({
  nome: z
    .string()
    .trim()
    .min(1, 'Informe seu nome completo.')
    .refine((n) => n.split(/\s+/).filter(Boolean).length >= 2, {
      message: 'Informe o nome completo (nome e sobrenome).',
    }),
  emailAddress: z.string().trim().min(1, 'Informe seu e-mail.').email('E-mail inválido.'),
  telefone: z
    .string()
    .trim()
    .refine((t) => telefoneValido(t), {
      message: 'Informe o telefone com DDD.',
    }),
  // Texto, não enum: o placeholder do select é `''`, e o tipo do zod para
  // enum com string vazia perde o `''` — o formulário nem compilaria com ele.
  // O `includes` (e não `ehVinculo`) é para o `refine` não virar predicado de
  // tipo e estreitar a saída, o que devolveria o mesmo problema.
  vinculo: z.string().refine((v) => (VINCULOS_COM_A_EMPRESA as readonly string[]).includes(v), {
    message: 'Informe o seu vínculo com a empresa.',
  }),
  perfilAssessor: z.boolean(),
});

type CamposIdentificacao = z.infer<typeof esquemaIdentificacao>;

function FormularioDeIdentificacao(props: {
  readonly aoConfirmar: (quem: Identificacao) => void;
}): JSX.Element {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CamposIdentificacao>({
    resolver: zodResolver(esquemaIdentificacao),
    defaultValues: { nome: '', emailAddress: '', telefone: '', vinculo: '', perfilAssessor: false },
    mode: 'onSubmit',
  });

  const enviar = handleSubmit((valores) => {
    // O esquema já recusou o que não é vínculo; a guarda é para o tipo.
    if (!ehVinculo(valores.vinculo)) return;
    props.aoConfirmar({
      nome: valores.nome,
      emailAddress: valores.emailAddress,
      telefone: valores.telefone,
      vinculo: valores.vinculo,
      perfilAssessor: valores.perfilAssessor,
    });
  });

  return (
    <Cartao
      como="section"
      titulo="Antes de calcular, quem é você?"
      subtitulo="Enviamos o resultado por e-mail e um consultor entra em contato para explicar como aproveitar o crédito."
    >
      <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Nome completo"
            autoComplete="name"
            erro={errors.nome?.message}
            {...register('nome')}
          />
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="email"
            erro={errors.emailAddress?.message}
            {...register('emailAddress')}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={control}
            name="telefone"
            render={({ field }) => (
              <CampoTelefone
                rotulo="Telefone com DDD"
                erro={errors.telefone?.message}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          <CampoDeSelecao
            rotulo="Vínculo com a empresa que será calculada"
            obrigatorio
            placeholder="Selecione"
            opcoes={VINCULOS_COM_A_EMPRESA.map((v) => ({ valor: v, rotulo: v }))}
            erro={errors.vinculo?.message}
            {...register('vinculo')}
          />
        </div>

        {/*
          O mesmo texto do Deca 1. Marcar não cria nada agora: o cadastro que
          este lead finalizar é que entra em "Em aprovação" em vez de ser
          aprovado sozinho.
        */}
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            {...register('perfilAssessor')}
          />
          <span className="flex flex-col">
            <span className="text-sm text-conteudo-primario">
              Gerencio uma carteira de empresas distintas que adquirem Créditos de ICMS
            </span>
            <span className="text-xs text-conteudo-sutil">
              Marque se você atua como assessor ou parceiro, e não pela própria empresa.
            </span>
          </span>
        </label>

        <div>
          <Botao type="submit">Continuar para o cálculo</Botao>
        </div>
      </form>
    </Cartao>
  );
}

/**
 * Lembra de quem é a simulação em curso, e oferece a saída.
 *
 * Trocar de pessoa aqui é o mesmo que recomeçar: o cálculo seguinte pertence a
 * outro lead, e reaproveitar o contato anterior é exatamente o que o Deca 1
 * fazia de errado.
 */
function QuemEsta(props: {
  readonly quem: Identificacao;
  readonly aoTrocar: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda bg-superficie-vidro px-4 py-3 text-sm">
      <p className="text-conteudo-secundario">
        Simulando para <strong className="text-conteudo-primario">{props.quem.nome}</strong> —{' '}
        {props.quem.emailAddress}
      </p>
      <Botao variante="sutil" tamanho="pequeno" onClick={props.aoTrocar}>
        Não sou eu
      </Botao>
    </div>
  );
}

/* ─── Avisos da leitura ───────────────────────────────────────────────────── */

/** O que cada aviso quer dizer para quem não conhece o layout do SPED. */
const EXPLICACAO_DO_AVISO: Readonly<Record<string, string>> = {
  E111_IGNORADO:
    'O arquivo tem ajustes de apuração que não são o crédito do SISCRED. ' +
    'Eles não entram na conta, e isso é o esperado — estorno de crédito e outros ' +
    'débitos convivem no mesmo registro.',
  E110_AUSENTE:
    'Sem o registro de apuração, o mês fica zerado. Normalmente é arquivo ' +
    'incompleto ou truncado no envio.',
  REGISTRO_0005_AUSENTE:
    'Endereço e telefone não vieram no arquivo. Não afeta o cálculo; só o ' +
    'preenchimento automático do cadastro.',
};

/**
 * Um aviso por tipo, não um por arquivo.
 *
 * O parser avisa por arquivo, então doze SPEDs com ajuste de outro código
 * produziam doze linhas idênticas — ruído que esconde um aviso diferente no
 * meio, e chaves repetidas no React. Aqui os iguais viram um, com a contagem de
 * arquivos ao lado.
 *
 * O tom também muda: `E111_IGNORADO` é informação, não problema. Vestir de
 * atenção o que acontece em quase todo arquivo ensina a ignorar o quadro
 * inteiro, e aí o aviso que importa passa batido.
 */
function AvisosDaLeitura(props: {
  readonly avisos: readonly { readonly codigo: string; readonly mensagem: string }[];
}): JSX.Element {
  const porCodigo = new Map<string, { mensagem: string; arquivos: number }>();
  for (const aviso of props.avisos) {
    const atual = porCodigo.get(aviso.codigo);
    porCodigo.set(aviso.codigo, {
      mensagem: aviso.mensagem,
      arquivos: (atual?.arquivos ?? 0) + 1,
    });
  }

  const itens = [...porCodigo.entries()];
  const soInformativo = itens.every(([codigo]) => codigo === 'E111_IGNORADO');

  return (
    <Aviso
      tom={soInformativo ? 'informacao' : 'atencao'}
      titulo={soInformativo ? 'Sobre a leitura dos arquivos' : 'Avisos da leitura dos arquivos'}
    >
      <ul className="flex flex-col gap-3">
        {itens.map(([codigo, { arquivos, mensagem }]) => (
          <li key={codigo}>
            <p>
              <span className="valor-fiscal text-xs">{codigo}</span>
              {arquivos > 1 && <span className="text-conteudo-sutil"> · {arquivos} arquivos</span>}
            </p>
            <p className="mt-0.5">{EXPLICACAO_DO_AVISO[codigo] ?? mensagem}</p>
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
